import { useLingui } from "@lingui/react/macro";
import { Alert, List, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";

export const Route = createFileRoute("/docs/downloads/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useLingui();

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`What is downloads?`}</Title>
      <Text>
        {t`REALity provides downloads of useful data from Lantmäteriet and other approved official sources. Depending on the service configuration and your permissions, downloads may include:`}
      </Text>
      <List withPadding>
        <List.Item>{t`Property-related information.`}</List.Item>
        <List.Item>{t`Geographical data.`}</List.Item>
        <List.Item>{t`Address information.`}</List.Item>
        <List.Item>{t`Property maps or map extracts.`}</List.Item>
        <List.Item>{t`Lists of property owners or addresses.`}</List.Item>
        <List.Item>{t`Data for further processing in Excel, GIS or other applications.`}</List.Item>
        <List.Item>{t`Exported reports, such as PDF files.`}</List.Item>
      </List>
      <Text>
        {t`The available download formats and datasets may vary over time. Always use the dataset and download option that best matches your assignment’s purpose.`}
      </Text>
      {/* TODO: screenshot of the downloads page. */}

      <DocHeading order={2} size="h3">{t`Common download types`}</DocHeading>
      <List withPadding>
        <List.Item>{t`A PDF report for one property.`}</List.Item>
        <List.Item>{t`An Excel file containing information about several properties.`}</List.Item>
        <List.Item>{t`A list of property owners or addresses.`}</List.Item>
        <List.Item>{t`A map or map extract.`}</List.Item>
        <List.Item>{t`Geographical data for use in a GIS application.`}</List.Item>
        <List.Item>{t`Structured data for further analysis.`}</List.Item>
      </List>

      <DocHeading order={2} size="h3">{t`How to download data`}</DocHeading>
      <List type="ordered" withPadding>
        <List.Item>{t`Search for or select the relevant properties.`}</List.Item>
        <List.Item>{t`Review the result before downloading.`}</List.Item>
        <List.Item>{t`Select the required information or dataset.`}</List.Item>
        <List.Item>{t`Choose the appropriate output format.`}</List.Item>
        <List.Item>{t`Confirm the assignment or project.`}</List.Item>
        <List.Item>{t`Start the download.`}</List.Item>
        <List.Item>{t`Store the result in an approved Sweco location.`}</List.Item>
        <List.Item>{t`Record or document the source and date if the data will be used in a deliverable.`}</List.Item>
      </List>
      {/* TODO: screenshots of the download steps. */}

      <DocHeading
        order={2}
        size="h3"
      >{t`Before downloading large datasets`}</DocHeading>
      <Text>{t`Before downloading a large dataset, check whether:`}</Text>
      <List withPadding>
        <List.Item>{t`You need all selected properties.`}</List.Item>
        <List.Item>{t`You need all available information.`}</List.Item>
        <List.Item>{t`A smaller geographical area would be sufficient.`}</List.Item>
        <List.Item>{t`A simpler download format would meet the requirement.`}</List.Item>
        <List.Item>{t`The data can be retrieved through an existing Sweco application instead.`}</List.Item>
      </List>
      <Alert variant="light" color="yellow">
        {t`Do not share downloaded data outside the permitted customer or assignment context.`}
      </Alert>
    </DocsContent>
  );
}
